import { useCallback } from 'react';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { Api } from 'src/models/Api.model';
import { getLocaleCode, Language } from 'src/models/Language.model';

import useGetRetinalExamDataModelId from 'src/hooks/useGetRetinalExamDataModelConfig';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import useUiTranslation from 'src/hooks/useUiTranslation';

import getSessionsImageFiles from 'src/utils/getSessionsImageFiles';

export default function useGetSessions() {
  const { deviceDataModelId } = useGetRetinalExamDataModelId();

  const { handleHttpRequestError } = useHandleHttpRequestError();
  const { setTotalLeftImageAttempt, setTotalRightImageAttempt } =
    useRemoteBoundStore();

  const { tCommon, language } = useUiTranslation();

  const getSessions = useCallback(
    async (iid: string) => {
      try {
        const sessions = await Api.remote.find_retake_sessions(
          deviceDataModelId,
          iid,
        );

        if (sessions.length === 0) {
          setTotalLeftImageAttempt(0);
          setTotalRightImageAttempt(0);
          return;
        }

        const { leftImageFileContainer, rightImageFileContainer } =
          getSessionsImageFiles(sessions);

        setTotalLeftImageAttempt(leftImageFileContainer.length);
        setTotalRightImageAttempt(rightImageFileContainer.length);
      } catch (error) {
        const errorMessage = handleHttpRequestError(error);
        Api.alertBox({
          title: tCommon('bootbox.title.Error'),
          message: `Failed to find retake sessions. ${errorMessage}`,
          locale: getLocaleCode(language as Language),
          type: 'error',
        });
      }
    },
    [
      deviceDataModelId,
      handleHttpRequestError,
      language,
      setTotalLeftImageAttempt,
      setTotalRightImageAttempt,
      tCommon,
    ],
  );

  return {
    getSessions,
  };
}
